import { motion } from "framer-motion"; // v11 compatible API [web:20]
import { useState, useEffect } from "react";


const PALETTE = {
  ivory: "#F7F1E8",
  charcoal: "#1B1B1B",
  tan: "#C3A585",
  tanHover: "#b59473",
  textDark: "#2A2A2A",
  textLight: "#FFFFFF",
  subDark: "#6C6C6C",
  subLight: "#D2D2D2",
  lineLight: "#DCD2C5",
  lineDark: "rgba(255,255,255,0.18)",
};

const heading = {
  hidden: { opacity: 0, y: -30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

// Typing effect component for bullet points
const TypingText = ({ text, delay = 0, speed = 30 }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    if (!isInView) return;
    
    let currentIndex = 0;
    const timeoutId = setTimeout(() => {
      const intervalId = setInterval(() => {
        if (currentIndex <= text.length) {
          setDisplayedText(text.slice(0, currentIndex));
          currentIndex++;
        } else {
          clearInterval(intervalId);
        }
      }, speed);
      
      return () => clearInterval(intervalId);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [text, delay, speed, isInView]);

  return (
    <motion.span
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      onViewportEnter={() => setIsInView(true)}
    >
      {displayedText}
      {displayedText.length < text.length && (
        <span className="animate-pulse">|</span>
      )}
    </motion.span>
  );
};



const Page = () => {
  return (
    <motion.main
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="bg-neutral-100 pt-[65px]"
      style={{ minHeight: "800px" }}
    >

<div className="px-4 pt-12 sm:px-6 lg:px-8 flex flex-col justify-center items-center text-center">
  <motion.h2
    variants={heading}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0.5 }}
    className="text-2xl sm:text-5xl font-extrabold tracking-tight"
    style={{ color: PALETTE.textDark }}
  >    <span style={{ color: PALETTE.textDark }} className="tracking-widest">

    COORDINATION - MANAGEMENT
    </span>
  </motion.h2>
</div>


<section className="mx-auto max-w-7xl px-4 py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 bg-canvas-300">
          {/* Left column - heading and overlapping image */}
          <motion.div 
            className="lg:col-span-7"
            initial={{ x: -100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
<div className="flex items-center gap-8 bg-canvas-300 p-8 sm:p-12">
  {/* Logo at left */}
  <div className="flex-shrink-0 w-24 h-24  rounded flex items-center justify-center">
    <img
      src="./images/m1.png"
      alt="PLATINUM GROUP logo"
      className="max-h-full max-w-full object-contain"
    />
  </div>
  {/* Details at right */}
  <div>
    <div className="text-lg font-semibold text-neutral-700 mb-1">PLATINUM GROUP</div>
    <div className="text-base text-neutral-600 mb-1">s/no 514, charholi</div>
    <div className="text-base mb-1">
      <span className="font-bold italic">
        B+G+P+ 22 floors RESIDENTIAL HIGH RISE 9,00,000 sq.ft
      </span>
    </div>
    <div className="text-base text-neutral-700">
      STATUS : <span className="font-bold">ONGOING</span>
    </div>
  </div>
</div>


            {/* Image intruding into right block */}
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative aspect-[14/11] w-full overflow-hidden lg:pr-10 "
            >
              <img
                src={"./images/m/1.png"}
                alt="Interior living room"
                className="h-full w-full object-cover lg:translate-x-6 xl:translate-x-10 rounded-tr-md shadow"
              />
              {/* Optional subtle seam shadow */}
              {/* Red Note */}
              <div className="absolute bottom-2 left-2 right-2 left-8 right-12 bg-white/75 p-2 rounded">
                <p className="text-red-500 text-xs font-semibold text-center">
                  PLEASE NOTE: THE IMAGES ARE A GRAPHICAL REPRESENTATION 3D RENDERING, ACTUAL IMAGES OF THE PROJECT ARE WIP.
                </p>
              </div>            </motion.div>
          </motion.div>

          {/* Right column */}
          <motion.div 
            className="lg:col-span-5 bg-charcoal-900   text-white flex items-center lg:-ml-6 xl:-ml-10"
            initial={{ x: 100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="   px-6 py-10           /* base (mobile) */
  sm:px-8 sm:py-12     /* small screens */
  md:px-10 md:py-14    /* medium */
  lg:px-12 lg:py-16    /* desktop */
  xl:px-16 xl:py-20    /* large desktop */
  2xl:px-20 2xl:py-24  /* very wide */
  max-w-xl">
                  <motion.p
                initial={{ y: -120, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-sm px-6  tracking-wide "
              >
    CHALLENGE : Disconnected workflows between consultants often lead to design clashes, delayed decisions, and inefficiencies on-site.
    <span className="block mt-4">
      OUR APPROACH : We bring architecture, structure, MEP, landscape, and interiors in a unified timeline through proactive coordination.
</span>
              </motion.p>

              <motion.h2
                initial={{ y: -120, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className=" mt-6 px-6 sm:text-3xl tracking-wide font-semibold "
              >
              Scope of work
              </motion.h2>

<motion.ul
  initial={{ y: 12, opacity: 0 }}
  whileInView={{ y: 0, opacity: 1 }}
  viewport={{ once: true }}
  transition={{ delay: 0.1, duration: 0.5 }}
  className="mt-6 px-6 max-w-md leading-7 text-white/90  list-disc list-inside text-sm"
>
  <motion.li>
    <TypingText 
      text="Coordinate and involve with the entire brainstorming process with branding, media and with Sales Team for the project."
      delay={200}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Schedule and Lead Joint Consultant Design Coordination Meetings:"
      delay={400}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Prepare Meeting Agendas, Minutes, and Track Action Items: Create Formats for Trackers and Coordination Documentation:"
      delay={600}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Coordinate with Consultants for Design Development, GFC Drawings, and Project Enhancements:"
      delay={800}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Finalize Project Specifications and Obtain Client Approval:"
      delay={1000}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Liaise with Sanctioning and Environmental Consultants:"
      delay={1200}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Conduct Site Audits and Visits to Monitor Progress:"
      delay={1400}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Record Design Changes, Modifications, and Revisions:"
      delay={1600}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Monitor and Manage Communication Channels:"
      delay={1800}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Track Consultant Payment Releases:"
      delay={2000}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Provide Expert Guidance Based on Past Experience:"
      delay={2200}
    />
  </motion.li>
</motion.ul>
            </div>
          </motion.div>
        </div>
      </section>



{/* ------------------------------------------- */}



<section className="mx-auto max-w-7xl px-4 py-12 lg:py-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 bg-canvas-300">
          {/* Left column - heading and overlapping image */}
          <motion.div 
            className="lg:col-span-7"
            initial={{ x: -100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
<div className="flex items-center gap-8 bg-canvas-300 p-8 sm:p-12">
  {/* Logo at left */}
  <div className="flex-shrink-0 w-24 h-24  rounded flex items-center justify-center">
    <img
      src="./images/m1.png"
      alt="PLATINUM GROUP logo"
      className="max-h-full max-w-full object-contain"
    />
  </div>
  {/* Details at right */}
  <div>
    <div className="text-lg font-semibold text-neutral-700 mb-1">PLATINUM GROUP</div>
    <div className="text-base text-neutral-600 mb-1">SR.NO 338 , UPPER DHANORI 
</div>
    <div className="text-base mb-1">
      <span className="font-bold italic">
B+G+P+ 22 floors RESIDENTIAL HIGH RISE 16,00,00 sq.ft
    </span>
    </div>
    <div className="text-base text-neutral-700">
      STATUS : ONGOING
    </div>
  </div>
</div>


            {/* Image intruding into right block */}
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative aspect-[14/11] w-full overflow-hidden lg:pr-10 "
            >
              <img
                src={"./images/m/2.png"}
                alt="Interior living room"
                className="h-full w-full object-cover lg:translate-x-6 xl:translate-x-10 rounded-tr-md shadow"
              />
              {/* Optional subtle seam shadow */}
              {/* Red Note */}
              <div className="absolute bottom-2 left-2 right-2 left-8 right-12 bg-white/75 p-2 rounded">
                <p className="text-red-500 text-xs font-semibold text-center">
                  PLEASE NOTE: THE IMAGES ARE A GRAPHICAL REPRESENTATION 3D RENDERING, ACTUAL IMAGES OF THE PROJECT ARE WIP.
                </p>
              </div>            </motion.div>
          </motion.div>

          {/* Right column */}
          <motion.div 
            className="lg:col-span-5 bg-charcoal-900   text-white flex items-center lg:-ml-6 xl:-ml-10"
            initial={{ x: 100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="   px-6 py-10           /* base (mobile) */
  sm:px-8 sm:py-12     /* small screens */
  md:px-10 md:py-14    /* medium */
  lg:px-12 lg:py-16    /* desktop */
  xl:px-16 xl:py-20    /* large desktop */
  2xl:px-20 2xl:py-24  /* very wide */
  max-w-xl">


              <motion.h2
                initial={{ y: -120, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className=" mt-6 px-6 sm:text-3xl tracking-wide font-semibold "
              >
              Scope of work
              </motion.h2>

<motion.ul
  initial={{ y: 12, opacity: 0 }}
  whileInView={{ y: 0, opacity: 1 }}
  viewport={{ once: true }}
  transition={{ delay: 0.1, duration: 0.5 }}
  className="mt-6 px-6 max-w-md leading-7 text-white/90  list-disc list-inside text-sm"
>
  <motion.li>
    <TypingText 
      text="Coordinate and involve with the entire brainstorming process with branding, media and with Sales Team for the project."
      delay={200}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Schedule and Lead Joint Consultant Design Coordination Meetings:"
      delay={400}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Prepare Meeting Agendas, Minutes, and Track Action Items: Create Formats for Trackers and Coordination Documentation:"
      delay={600}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Coordinate with Consultants for Design Development, GFC Drawings, and Project Enhancements:"
      delay={800}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Finalize Project Specifications and Obtain Client Approval:"
      delay={1000}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Liaise with Sanctioning and Environmental Consultants:"
      delay={1200}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Conduct Site Audits and Visits to Monitor Progress:"
      delay={1400}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Record Design Changes, Modifications, and Revisions:"
      delay={1600}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Monitor and Manage Communication Channels:"
      delay={1800}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Track Consultant Payment Releases:"
      delay={2000}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Provide Expert Guidance Based on Past Experience:"
      delay={2200}
    />
  </motion.li>
</motion.ul>
            </div>
          </motion.div>
        </div>
      </section>



<section className="mx-auto max-w-7xl px-4 py-12 lg:py-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 bg-canvas-300">
          {/* Left column - heading and overlapping image */}
          <motion.div 
            className="lg:col-span-7"
            initial={{ x: -100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
<div className="flex items-center gap-8 bg-canvas-300 p-8 sm:p-12">
  {/* Logo at left */}
  <div className="flex-shrink-0 w-24 h-24  rounded flex items-center justify-center">
    <img
      src="./images/m1.png"
      alt="PLATINUM GROUP logo"
      className="max-h-full max-w-full object-contain"
    />
  </div>
  {/* Details at right */}
  <div>
    <div className="text-lg font-semibold text-neutral-700 mb-1">PLATINUM GROUP</div>
    <div className="text-base text-neutral-600 mb-1">B+G+ 14 floors 
 
</div>
    <div className="text-base mb-1">
      <span className="font-bold italic">
 RESIDENTIAL PROJECT 
2,90,000 sq.ft

    </span>
    </div>
    <div className="text-base text-neutral-700">
      STATUS : ONGOING
    </div>
  </div>
</div>


            {/* Image intruding into right block */}
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative aspect-[14/11] w-full overflow-hidden lg:pr-10 "
            >
              <img
                src={"./images/m/3.png"}
                alt="Interior living room"
                className="h-full w-full object-cover lg:translate-x-6 xl:translate-x-10 rounded-tr-md shadow"
              />
              {/* Optional subtle seam shadow */}
              {/* Red Note */}
              <div className="absolute bottom-2 left-2 right-2 left-8 right-12 bg-white/75 p-2 rounded">
                <p className="text-red-500 text-xs font-semibold text-center">
                  PLEASE NOTE: THE IMAGES ARE A GRAPHICAL REPRESENTATION 3D RENDERING, ACTUAL IMAGES OF THE PROJECT ARE WIP.
                </p>
              </div>            </motion.div>
          </motion.div>

          {/* Right column */}
          <motion.div 
            className="lg:col-span-5 bg-charcoal-900   text-white flex items-center lg:-ml-6 xl:-ml-10"
            initial={{ x: 100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="   px-6 py-10           /* base (mobile) */
  sm:px-8 sm:py-12     /* small screens */
  md:px-10 md:py-14    /* medium */
  lg:px-12 lg:py-16    /* desktop */
  xl:px-16 xl:py-20    /* large desktop */
  2xl:px-20 2xl:py-24  /* very wide */
  max-w-xl">


              <motion.h2
                initial={{ y: -120, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className=" mt-6 px-6 sm:text-3xl tracking-wide font-semibold "
              >
              Scope of work
              </motion.h2>

<motion.ul
  initial={{ y: 12, opacity: 0 }}
  whileInView={{ y: 0, opacity: 1 }}
  viewport={{ once: true }}
  transition={{ delay: 0.1, duration: 0.5 }}
  className="mt-6 px-6 max-w-md leading-7 text-white/90  list-disc list-inside text-sm"
>
  <motion.li>
    <TypingText 
      text="Coordinate and involve with the entire brainstorming process with branding, media and with Sales Team for the project."
      delay={200}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Schedule and Lead Joint Consultant Design Coordination Meetings:"
      delay={400}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Prepare Meeting Agendas, Minutes, and Track Action Items: Create Formats for Trackers and Coordination Documentation:"
      delay={600}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Coordinate with Consultants for Design Development, GFC Drawings, and Project Enhancements:"
      delay={800}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Finalize Project Specifications and Obtain Client Approval:"
      delay={1000}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Liaise with Sanctioning and Environmental Consultants:"
      delay={1200}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Conduct Site Audits and Visits to Monitor Progress:"
      delay={1400}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Record Design Changes, Modifications, and Revisions:"
      delay={1600}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Monitor and Manage Communication Channels:"
      delay={1800}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Track Consultant Payment Releases:"
      delay={2000}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Provide Expert Guidance Based on Past Experience:"
      delay={2200}
    />
  </motion.li>
</motion.ul>
            </div>
          </motion.div>
        </div>
      </section>




<section className="mx-auto max-w-7xl px-4 py-12 lg:py-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 bg-canvas-300">
          {/* Left column - heading and overlapping image */}
          <motion.div 
            className="lg:col-span-7"
            initial={{ x: -100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
<div className="flex items-center gap-8 bg-canvas-300 p-8 sm:p-12">
  {/* Logo at left */}
  <div className="flex-shrink-0 w-24 h-24  rounded flex items-center justify-center">
    <img
      src="./images/m2.png"
      alt="s logo"
      className="max-h-full max-w-full object-contain"
    />
  </div>
  {/* Details at right */}
  <div>
    <div className="text-lg font-semibold text-neutral-700 mb-1">ARTEMIS INFRATECH
</div>
    <div className="text-base text-neutral-600 mb-1">B+G+ 11 floors 

</div>
    <div className="text-base mb-1">
      <span className="font-bold italic">
RESIDENTIAL  PROJECT
1,50,000 sq.ft

      </span>
    </div>
    {/* <div className="text-base text-neutral-700">
      STATUS : ONGOING
    </div> */}
  </div>
</div>


            {/* Image intruding into right block */}
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative aspect-[14/11] w-full overflow-hidden lg:pr-10 "
            >
              <img
                src={"./images/m/4.png"}
                alt="Interior living room"
                className="h-full w-full object-cover lg:translate-x-6 xl:translate-x-10 rounded-tr-md shadow"
              />
              {/* Optional subtle seam shadow */}
              {/* Red Note */}
              <div className="absolute bottom-2 left-2 right-2 left-8 right-12 bg-white/75 p-2 rounded">
                <p className="text-red-500 text-xs font-semibold text-center">
                  PLEASE NOTE: THE IMAGES ARE A GRAPHICAL REPRESENTATION 3D RENDERING, ACTUAL IMAGES OF THE PROJECT ARE WIP.
                </p>
              </div>            </motion.div>
          </motion.div>

          {/* Right column */}
          <motion.div 
            className="lg:col-span-5 bg-charcoal-900   text-white flex items-center lg:-ml-6 xl:-ml-10"
            initial={{ x: 100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="   px-6 py-10           /* base (mobile) */
  sm:px-8 sm:py-12     /* small screens */
  md:px-10 md:py-14    /* medium */
  lg:px-12 lg:py-16    /* desktop */
  xl:px-16 xl:py-20    /* large desktop */
  2xl:px-20 2xl:py-24  /* very wide */
  max-w-xl">
                  <motion.p
                initial={{ y: -120, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-sm px-6  tracking-wide "
              >
    CHALLENGE : Disconnected workflows between consultants often lead to design clashes, delayed decisions, and inefficiencies on-site.
    <span className="block mt-4">
      OUR APPROACH : We bring architecture, structure, MEP, landscape, and interiors in a unified timeline through proactive coordination.
</span>
              </motion.p>

              <motion.h2
                initial={{ y: -120, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className=" mt-6 px-6 sm:text-3xl tracking-wide font-semibold "
              >
              Scope of work
              </motion.h2>

<motion.ul
  initial={{ y: 12, opacity: 0 }}
  whileInView={{ y: 0, opacity: 1 }}
  viewport={{ once: true }}
  transition={{ delay: 0.1, duration: 0.5 }}
  className="mt-6 px-6 max-w-md leading-7 text-white/90  list-disc list-inside text-sm"
>
  <motion.li>
    <TypingText 
      text="Coordinate and involve with the entire brainstorming process with branding, media and with Sales Team for the project."
      delay={200}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Schedule and Lead Joint Consultant Design Coordination Meetings:"
      delay={400}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Prepare Meeting Agendas, Minutes, and Track Action Items: Create Formats for Trackers and Coordination Documentation:"
      delay={600}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Coordinate with Consultants for Design Development, GFC Drawings, and Project Enhancements:"
      delay={800}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Finalize Project Specifications and Obtain Client Approval:"
      delay={1000}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Liaise with Sanctioning and Environmental Consultants:"
      delay={1200}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Conduct Site Audits and Visits to Monitor Progress:"
      delay={1400}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Record Design Changes, Modifications, and Revisions:"
      delay={1600}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Monitor and Manage Communication Channels:"
      delay={1800}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Track Consultant Payment Releases:"
      delay={2000}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Provide Expert Guidance Based on Past Experience:"
      delay={2200}
    />
  </motion.li>
</motion.ul>
            </div>
          </motion.div>
        </div>
      </section>




<section className="mx-auto max-w-7xl px-4 py-12 lg:py-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 bg-canvas-300">
          {/* Left column - heading and overlapping image */}
          <motion.div 
            className="lg:col-span-7"
            initial={{ x: -100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
<div className="flex items-center gap-8 bg-canvas-300 p-8 sm:p-12">
  {/* Logo at left */}
  <div className="flex-shrink-0 w-24 h-24  rounded flex items-center justify-center">
    <img
      src="./images/m2.png"
      alt="s logo"
      className="max-h-full max-w-full object-contain"
    />
  </div>
  {/* Details at right */}
  <div>
    <div className="text-lg font-semibold text-neutral-700 mb-1">ARTEMIS INFRATECH</div>
    <div className="text-base text-neutral-600 mb-1">B+G+ 13 floors 
</div>
    <div className="text-base mb-1">
      <span className="font-bold italic">
 RESIDENTIAL PROJECT
2,00,000 sq.ft

      </span>
    </div>
    {/* <div className="text-base text-neutral-700">
      STATUS : <span className="font-bold">ONGOING</span>
    </div> */}
  </div>
</div>


            {/* Image intruding into right block */}
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative aspect-[14/11] w-full overflow-hidden lg:pr-10 "
            >
              <img
                src={"./images/m/5.png"}
                alt="Interior living room"
                className="h-full w-full object-cover lg:translate-x-6 xl:translate-x-10 rounded-tr-md shadow"
              />
              {/* Optional subtle seam shadow */}
              {/* Red Note */}
              <div className="absolute bottom-2 left-2 right-2 left-8 right-12 bg-white/75 p-2 rounded">
                <p className="text-red-500 text-xs font-semibold text-center">
                  PLEASE NOTE: THE IMAGES ARE A GRAPHICAL REPRESENTATION 3D RENDERING, ACTUAL IMAGES OF THE PROJECT ARE WIP.
                </p>
              </div>            </motion.div>
          </motion.div>

          {/* Right column */}
          <motion.div 
            className="lg:col-span-5 bg-charcoal-900   text-white flex items-center lg:-ml-6 xl:-ml-10"
            initial={{ x: 100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="   px-6 py-10           /* base (mobile) */
  sm:px-8 sm:py-12     /* small screens */
  md:px-10 md:py-14    /* medium */
  lg:px-12 lg:py-16    /* desktop */
  xl:px-16 xl:py-20    /* large desktop */
  2xl:px-20 2xl:py-24  /* very wide */
  max-w-xl">
                  <motion.p
                initial={{ y: -120, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-sm px-6  tracking-wide "
              >
    CHALLENGE : Disconnected workflows between consultants often lead to design clashes, delayed decisions, and inefficiencies on-site.
    <span className="block mt-4">
      OUR APPROACH : We bring architecture, structure, MEP, landscape, and interiors in a unified timeline through proactive coordination.
</span>
              </motion.p>

              <motion.h2
                initial={{ y: -120, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className=" mt-6 px-6 sm:text-3xl tracking-wide font-semibold "
              >
              Scope of work
              </motion.h2>

<motion.ul
  initial={{ y: 12, opacity: 0 }}
  whileInView={{ y: 0, opacity: 1 }}
  viewport={{ once: true }}
  transition={{ delay: 0.1, duration: 0.5 }}
  className="mt-6 px-6 max-w-md leading-7 text-white/90  list-disc list-inside text-sm"
>
  <motion.li>
    <TypingText 
      text="Coordinate and involve with the entire brainstorming process with branding, media and with Sales Team for the project."
      delay={200}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Schedule and Lead Joint Consultant Design Coordination Meetings:"
      delay={400}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Prepare Meeting Agendas, Minutes, and Track Action Items: Create Formats for Trackers and Coordination Documentation:"
      delay={600}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Coordinate with Consultants for Design Development, GFC Drawings, and Project Enhancements:"
      delay={800}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Finalize Project Specifications and Obtain Client Approval:"
      delay={1000}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Liaise with Sanctioning and Environmental Consultants:"
      delay={1200}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Conduct Site Audits and Visits to Monitor Progress:"
      delay={1400}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Record Design Changes, Modifications, and Revisions:"
      delay={1600}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Monitor and Manage Communication Channels:"
      delay={1800}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Track Consultant Payment Releases:"
      delay={2000}
    />
  </motion.li>
  <motion.li>
    <TypingText 
      text="Provide Expert Guidance Based on Past Experience:"
      delay={2200}
    />
  </motion.li>
</motion.ul>
            </div>
          </motion.div>
        </div>
      </section>




    </motion.main>





  );
};

export default Page;
